import { useState, useCallback, useRef, useEffect } from 'react';
import { Message, AuthConfig, SendMessagePayload, SendMessageResponse } from '../types';
import { sendMessage as apiSendMessage } from '../utils/api';

export interface UseSendMessageOptions {
  sendMessageUrl: string;
  sessionId: string;
  authConfig?: AuthConfig;
  onMessageSent?: (message: Message) => void;
  onError?: (error: Error) => void;
  onSuccess?: (response: SendMessageResponse) => void;
}

/**
 * Hook para gerenciar envio de mensagens
 */
export const useSendMessage = ({
  sendMessageUrl,
  sessionId,
  authConfig,
  onMessageSent,
  onError,
  onSuccess,
}: UseSendMessageOptions) => {
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const isMountedRef = useRef(true);

  // Usa refs para callbacks e authConfig para evitar loop infinito
  const onMessageSentRef = useRef(onMessageSent);
  const onErrorRef = useRef(onError);
  const onSuccessRef = useRef(onSuccess);
  const authConfigRef = useRef(authConfig);

  // Atualiza refs quando callbacks mudam
  useEffect(() => {
    onMessageSentRef.current = onMessageSent;
    onErrorRef.current = onError;
    onSuccessRef.current = onSuccess;
    authConfigRef.current = authConfig;
  });

  /**
   * Envia uma mensagem para o backend
   */
  const sendMessage = useCallback(
    async (
      content: string,
      idempotencyKey?: string,
      metadata?: Record<string, any>
    ): Promise<SendMessageResponse | null> => {
      if (!content.trim() || !sessionId || !sendMessageUrl) {
        return null;
      }

      setIsSending(true);
      setError(null);

      const payload: SendMessagePayload = {
        message: content.trim(),
        sessionId,
        timestamp: new Date().toISOString(),
        idempotencyKey,
        metadata,
      };

      try {
        const response = await apiSendMessage(
          sendMessageUrl,
          payload,
          authConfigRef.current
        );

        if (!isMountedRef.current) return null;

        if (response.success) {
          if (response.message) {
            onMessageSentRef.current?.(response.message);
          }

          onSuccessRef.current?.(response);
        } else {
          throw new Error(response.error || 'Falha ao enviar mensagem');
        }

        return response;
      } catch (err) {
        if (!isMountedRef.current) return null;

        const error =
          err instanceof Error ? err : new Error('Erro ao enviar mensagem');
        setError(error);
        onErrorRef.current?.(error);
        throw error;
      } finally {
        if (isMountedRef.current) {
          setIsSending(false);
        }
      }
    },
    [sendMessageUrl, sessionId]
  );

  /**
   * Reseta o estado de erro
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    sendMessage,
    isSending,
    error,
    clearError,
  };
};
